import type { DataConfig, ProjectionConfig, SemanticIntent, WidgetType } from '../../../types/bi';
import { applyMeasureAliasPolicy } from './aliasPolicy';

function normaliseFieldKey(key: string): string {
  return key.replace(/^field_key:/, '');
}

/**
 * Builds a semantic_intent payload from legacy data_config.
 * This allows frontend query execution to opt into semantic intent while
 * keeping persisted widget payloads backwards-compatible.
 */
export function buildSemanticIntentFromDataConfig(
  dataConfig: DataConfig,
  widgetType: WidgetType,
  projectionConfig?: ProjectionConfig | null,
): SemanticIntent {
  const dimensions = (dataConfig.dimensions || []).map(normaliseFieldKey);
  const normalizedMeasures = (dataConfig.measures || []).map((m) => ({
    field_key: normaliseFieldKey(m.field_key),
    aggregation: m.aggregation,
    alias: m.alias,
  }));
  const measures = applyMeasureAliasPolicy(normalizedMeasures, 'manual');

  const primaryMeasureAlias = measures[0]?.alias;

  const primaryTimeDimension =
    projectionConfig?.type && dimensions.length > 0
      ? dimensions[0]
      : dataConfig.date_granularity && dimensions.length > 0
        ? dimensions.find((d) => dataConfig.date_granularity?.[d]) || dimensions[0]
        : undefined;

  return {
    visual_type: widgetType,
    source_table: dataConfig.source_table,
    roles: {
      category_fields: dimensions,
      value_fields: measures,
    },
    bindings: {
      primary_measure_alias: primaryMeasureAlias,
      primary_time_dimension: primaryTimeDimension,
      conditional_format_measure_alias: primaryMeasureAlias,
    },
    filters: dataConfig.filters,
    sorting: dataConfig.sort,
    limit: dataConfig.limit,
    comparative_date: dataConfig.comparative_date,
    calculated_metric_ids: dataConfig.calculated_metric_ids,
    date_granularity: dataConfig.date_granularity,
  };
}
